import React, { useState, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Plus, Search, Clock } from 'lucide-react'
import { format, parse, subMinutes, subHours } from 'date-fns'
import type { DateRange } from 'react-day-picker'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import RangeDayPicker from '@/components/ui/range-date-picker'
import { KeywordChip } from '@/components/scan'
import { useScanSubmit } from '@/lib/hooks/useScanSubmit'
import { scanFormSchema, type ScanFormData } from '@/lib/validation/schemas'
import type { TimeFramePreset } from '@/lib/types'
import { autoCorrectTime } from '@/lib/utils/timeFormat'
import { useCrisisStore } from '@/lib/stores/crisisStore'

const timeFrameOptions: Array<{ value: TimeFramePreset; label: string }> = [
  { value: 'last_15_minutes', label: 'Laatste 15 minuten' },
  { value: 'last_30_minutes', label: 'Laatste 30 minuten' },
  { value: 'last_1_hour', label: 'Laatste uur' },
  { value: 'custom', label: 'Aangepaste periode' },
]

interface ScanCreateModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export function ScanCreateModal({ open, onOpenChange, onSuccess }: ScanCreateModalProps) {
  const [currentKeyword, setCurrentKeyword] = useState('')
  const [range, setRange] = useState<DateRange | undefined>()
  const { activeCrisis } = useCrisisStore()

  // Calculate default dates for custom time frame
  const now = new Date()
  const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000)

  // Format dates in dd/mm/yyyy format
  const defaultStartDate = format(oneHourAgo, 'dd/MM/yyyy')
  const defaultStartTime = format(oneHourAgo, 'HH:mm')
  const defaultEndDate = format(now, 'dd/MM/yyyy')
  const defaultEndTime = format(now, 'HH:mm')

  const { submitScan, isSubmitting, error } = useScanSubmit()

  // Generate default keywords from active crisis
  // First keyword is always the location (editable), other keywords are regular
  const getDefaultKeywords = useCallback(() => {
    if (!activeCrisis) return []

    const keywords = []
    // Add crisis location as the FIRST keyword (always editable)
    if (activeCrisis.location) {
      keywords.push(activeCrisis.location)
    }
    // Add crisis type as a keyword
    if (activeCrisis.type) {
      keywords.push(activeCrisis.type)
    }
    return keywords
  }, [activeCrisis])

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    reset,
  } = useForm<ScanFormData>({
    resolver: zodResolver(scanFormSchema),
    defaultValues: {
      keywords: getDefaultKeywords(),
      timeFrame: {
        preset: 'last_1_hour',
        fromDate: defaultStartDate,
        toDate: defaultEndDate,
        fromTime: defaultStartTime,
        toTime: defaultEndTime,
      },
    },
  })

  const keywords = watch('keywords') || []
  const timeFramePreset = watch('timeFrame.preset')

  // Reset form when modal opens or closes
  useEffect(() => {
    if (!open) {
      // Reset form when closing
      reset()
      setCurrentKeyword('')
      setRange(undefined)
    } else {
      // Set default keywords when opening
      setValue('keywords', getDefaultKeywords())
    }
  }, [open, reset, activeCrisis, setValue, getDefaultKeywords])

  // Initialize custom date fields when preset changes to 'custom'
  useEffect(() => {
    if (timeFramePreset === 'custom') {
      const fromDate = parse(defaultStartDate, 'dd/MM/yyyy', new Date())
      const toDate = parse(defaultEndDate, 'dd/MM/yyyy', new Date())
      setRange({ from: fromDate, to: toDate })
      setValue('timeFrame.fromDate', defaultStartDate)
      setValue('timeFrame.toDate', defaultEndDate)
      setValue('timeFrame.fromTime', defaultStartTime)
      setValue('timeFrame.toTime', defaultEndTime)
    }
  }, [
    timeFramePreset,
    setValue,
    defaultStartDate,
    defaultEndDate,
    defaultStartTime,
    defaultEndTime,
  ])

  // Sync range to form values
  useEffect(() => {
    if (range?.from && range?.to) {
      setValue('timeFrame.fromDate', format(range.from, 'dd/MM/yyyy'))
      setValue('timeFrame.toDate', format(range.to, 'dd/MM/yyyy'))
    }
  }, [range, setValue])

  const addKeyword = (keyword: string) => {
    const trimmed = keyword.trim()
    if (trimmed && !keywords.includes(trimmed)) {
      setValue('keywords', [...keywords, trimmed])
      setCurrentKeyword('')
    }
  }

  const removeKeyword = (index: number) => {
    setValue(
      'keywords',
      keywords.filter((_: string, i: number) => i !== index)
    )
  }

  const editKeyword = (index: number, newValue: string) => {
    const trimmed = newValue.trim()
    if (trimmed && !keywords.includes(trimmed)) {
      const updatedKeywords = [...keywords]
      updatedKeywords[index] = trimmed
      setValue('keywords', updatedKeywords)
    }
  }

  const handleKeywordKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      addKeyword(currentKeyword)
    }
  }

  const handleTimeBlur =
    (field: 'timeFrame.fromTime' | 'timeFrame.toTime') =>
    (e: React.FocusEvent<HTMLInputElement>) => {
      const correctedTime = autoCorrectTime(e.target.value)
      if (correctedTime) {
        setValue(field, correctedTime)
      }
    }

  const onSubmit = async (data: ScanFormData) => {
    console.debug('Submitting scan with data:', data)

    // Convert preset timeframes to actual date ranges
    if (data.timeFrame.preset !== 'custom') {
      const now = new Date()
      let start: Date

      switch (data.timeFrame.preset) {
        case 'last_15_minutes':
          start = subMinutes(now, 15)
          break
        case 'last_30_minutes':
          start = subMinutes(now, 30)
          break
        case 'last_45_minutes':
          start = subMinutes(now, 45)
          break
        case 'last_1_hour':
          start = subHours(now, 1)
          break
        default:
          start = subHours(now, 1) // fallback to 1 hour
      }

      data.timeFrame.from = start
      data.timeFrame.to = now
    }

    const success = await submitScan(data)
    if (success) {
      onOpenChange(false)
      if (onSuccess) {
        onSuccess()
      }
    }
  }

  const isFormValid = keywords.length > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto rounded-lg">
        <DialogHeader>
          <DialogTitle className="text-2xl">Nieuwe omgevingsanalyse</DialogTitle>
          <DialogDescription>
            Configureer de parameters voor je sociale media en nieuws analyse
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Grid Layout for 2 blocks */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Keywords */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Search className="w-5 h-5" />
                  Trefwoorden
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Voeg trefwoorden toe die gemonitord moeten worden in sociale media en nieuws.
                    Het blauwe trefwoord is voor de locatie. Klik erop om het te bewerken en verbind
                    de woorden met een OR-operator. Andere trefwoorden worden automatisch met een OR
                    verbonden.{' '}
                  </p>

                  <div className="flex gap-2 mb-3 align-stretch">
                    <Input
                      value={currentKeyword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setCurrentKeyword(e.target.value)
                      }
                      onKeyPress={handleKeywordKeyPress}
                      placeholder="Voeg trefwoord toe..."
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="gradient"
                      onClick={() => addKeyword(currentKeyword)}
                      disabled={!currentKeyword.trim()}
                    >
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Current Keywords */}
                  {keywords.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {keywords.map((keyword: string, index: number) => {
                        // First keyword is always the location keyword (editable, not removable)
                        const isLocationKeyword = index === 0
                        return (
                          <KeywordChip
                            key={index}
                            keyword={keyword}
                            onRemove={isLocationKeyword ? undefined : () => removeKeyword(index)}
                            onEdit={
                              isLocationKeyword
                                ? newValue => editKeyword(index, newValue)
                                : undefined
                            }
                            variant={isLocationKeyword ? 'editable' : 'default'}
                          />
                        )
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-red-700 text-muted-foreground">
                      Geef minimaal 1 trefwoord op.
                    </p>
                  )}

                  {errors.keywords && (
                    <p className="text-sm text-destructive">{errors.keywords.message}</p>
                  )}

                  {/* Search Query Preview */}
                  {keywords.length > 0 && (
                    <div className="mt-4 p-3 bg-muted/50 rounded-md border border-border">
                      <Label className="text-xs font-semibold text-muted-foreground mb-1 block">
                        Zoekterm Preview:
                      </Label>
                      <code className="text-sm font-mono text-foreground">
                        {keywords.length === 1 ? (
                          `(${keywords[0]})`
                        ) : (
                          <>
                            ({keywords[0]}) AND ({keywords.slice(1).join(' OR ')})
                          </>
                        )}
                      </code>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Time Frame */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Clock className="w-5 h-5" />
                  Tijdperiode
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">
                  Stel de periode in voor de analyse van de gegevens.
                </p>
                <div className="space-y-3 mt-3">
                  {timeFrameOptions.map(option => (
                    <div key={option.value} className="space-y-3">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="radio"
                          value={option.value}
                          {...register('timeFrame.preset')}
                          className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                        />
                        <span className="text-sm font-medium">{option.label}</span>
                      </label>

                      {option.value === 'custom' && timeFramePreset === 'custom' && (
                        <div className="ml-6 space-y-3 p-4 bg-muted/30 rounded-md">
                          <div className="space-y-4">
                            <div className="space-y-1">
                              <RangeDayPicker value={range} onChange={setRange} />
                              <p className="text-xs text-muted-foreground italic">
                                Je kunt maximaal 3 dagen selecteren
                              </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="from-time" variant="required">
                                  Begintijd
                                </Label>
                                <Input
                                  id="from-time"
                                  type="text"
                                  placeholder="HH:MM"
                                  {...register('timeFrame.fromTime')}
                                  onBlur={handleTimeBlur('timeFrame.fromTime')}
                                  className="w-full"
                                  maxLength={5}
                                />
                              </div>
                              <div className="space-y-2">
                                <Label htmlFor="to-time" variant="required">
                                  Eindtijd
                                </Label>
                                <Input
                                  id="to-time"
                                  type="text"
                                  placeholder="HH:MM"
                                  {...register('timeFrame.toTime')}
                                  onBlur={handleTimeBlur('timeFrame.toTime')}
                                  className="w-full"
                                  maxLength={5}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {errors.timeFrame && (
                  <p className="text-sm text-destructive mt-1">{errors.timeFrame.message}</p>
                )}
              </CardContent>
            </Card>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          {/* Actions */}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="mt-2 md:mt-0"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              variant="gradient"
              disabled={!isFormValid || isSubmitting}
              loading={isSubmitting}
            >
              {isSubmitting ? 'Analyse starten...' : 'Analyse starten'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
